/**
 * NY SLA (New York State Liquor Authority) Price Posting Constants
 *
 * These constants define the attribute configurations and valid values
 * for NY SLA price posting exports.
 */

import AttributeTypeEnum from "./AttributeTypeEnum";

/**
 * Beverage type codes for NY SLA
 */
export enum NYSLABeverageTypeEnum {
    TABLE_RED = "TR",
    TABLE_WHITE = "TW",
    DESSERT_PORT = "DP",
    SPARKLING = "S",
    SPIRIT = "SP",
    BEER = "B",
    CIDER = "C",
}

/**
 * Alcohol content codes for NY SLA
 */
export enum NYSLAAlcoholCodeEnum {
    A = "A", // 0-7% alcohol
    B = "B", // 7-14% alcohol (most wines)
    C = "C", // 14-24% alcohol
    D = "D", // 24%+ alcohol
}

/**
 * Combo limitation codes
 */
export enum NYSLAComboLimitEnum {
    REGULAR = "R",
    COMBO = "C",
}

/**
 * NY Product flag
 */
export enum NYSLANYProductEnum {
    YES = "Y",
    NO = "N",
}

/**
 * Posting type codes
 */
export enum NYSLAPostTypeEnum {
    WHOLESALE_RETAIL = "WR",
}

/**
 * Brand registration status codes
 */
export enum NYSLABrandRegEnum {
    PENDING = "P",
    REGISTERED = "R",
}

/**
 * Label type codes
 */
export enum NYSLALabelTypeEnum {
    ORIGINAL = "O",
    PRIVATE = "P",
}

/**
 * Default values for optional fields
 */
export const NYSLA_DEFAULTS = {
    POST_TYPE: "WR",
    BRAND_REG: "P", // Pending
    LABEL_TYPE: "O", // Original
    DISTRIB_ID: "N", // None
    COMBO_LIM: "R", // Regular
    ALCOHOL: "B", // 7-14%
    PROOF: "NA",
    PRIM_INFO: "NA",
    NYS_PROD: "N",
    SUBPACK: 1,
    FULLCASE: "N",
    SPLIT_CHAR: "NA",
    FOB: "NA",
    NYC_SURCHARGE: 0,
    NYS_ITEM: 1,
};

/**
 * Calculates the NY SLA alcohol code from ABV percentage
 * @param abv - Alcohol by volume percentage (e.g., 13.5)
 * @returns NY SLA alcohol code (A, B, C, or D)
 */
export function getAlcoholCodeFromABV(abv: number | null | undefined): NYSLAAlcoholCodeEnum {
    if (abv == null || isNaN(abv)) {
        return NYSLAAlcoholCodeEnum.B; // Default for most wines
    }
    if (abv < 7) return NYSLAAlcoholCodeEnum.A; // 0-7%
    if (abv < 14) return NYSLAAlcoholCodeEnum.B; // 7-14% (most wines)
    if (abv < 24) return NYSLAAlcoholCodeEnum.C; // 14-24%
    return NYSLAAlcoholCodeEnum.D; // 24%+
}

/**
 * General wine attributes - visible to users, useful for any wine catalog
 * Note: These use proper capitalization for user-friendly display
 */
export const WINE_GENERAL_ATTRIBUTE_DEFINITIONS = [
    {
        name: "Vintage",
        type: AttributeTypeEnum.NUMBER,
        options: [] as string[],
        required: false,
        searchable: true,
        display_order: 1,
        is_hidden: false,
        description: "Vintage year (e.g., 2020)",
    },
    {
        name: "ABV",
        type: AttributeTypeEnum.NUMBER,
        options: [] as string[],
        required: false,
        searchable: true,
        display_order: 2,
        is_hidden: false,
        description: "Alcohol by volume percentage (e.g., 13.5)",
    },
    {
        name: "Proof",
        type: AttributeTypeEnum.NUMBER,
        options: [] as string[],
        required: false,
        searchable: false,
        display_order: 3,
        is_hidden: false,
        description: "Proof value (for spirits, = ABV × 2)",
    },
];

/**
 * NYSLA-specific attributes - hidden from normal UI, only needed for NY SLA export
 */
export const NYSLA_SPECIFIC_ATTRIBUTE_DEFINITIONS = [
    {
        name: "nysla_bev_type",
        type: AttributeTypeEnum.DROPDOWN,
        options: Object.values(NYSLABeverageTypeEnum),
        required: true,
        searchable: true,
        display_order: 10,
        is_hidden: true,
        description: "NY SLA Beverage type code (TR=Table Red, TW=Table White, DP=Dessert/Port, S=Sparkling)",
    },
    {
        name: "nysla_ttb_id",
        type: AttributeTypeEnum.TEXT,
        options: [] as string[],
        required: false,
        searchable: true,
        display_order: 11,
        is_hidden: true,
        description: "TTB (Tax & Trade Bureau) ID number",
    },
    {
        name: "nysla_combo_lim",
        type: AttributeTypeEnum.DROPDOWN,
        options: Object.values(NYSLAComboLimitEnum),
        required: false,
        searchable: false,
        display_order: 12,
        is_hidden: true,
        description: "NY SLA Combo limitation (R=Regular, C=Combo)",
    },
    {
        name: "nysla_prim_info",
        type: AttributeTypeEnum.TEXT,
        options: [] as string[],
        required: false,
        searchable: false,
        display_order: 13,
        is_hidden: true,
        description: "NY SLA Primary information",
    },
    {
        name: "nysla_nys_prod",
        type: AttributeTypeEnum.DROPDOWN,
        options: Object.values(NYSLANYProductEnum),
        required: false,
        searchable: false,
        display_order: 14,
        is_hidden: true,
        description: "NY SLA NYS product flag (Y=Yes, N=No)",
    },
    {
        name: "nysla_brand_reg",
        type: AttributeTypeEnum.DROPDOWN,
        options: Object.values(NYSLABrandRegEnum),
        required: false,
        searchable: false,
        display_order: 15,
        is_hidden: true,
        description: "NY SLA Brand registration status (P=Pending, R=Registered)",
    },
    {
        name: "nysla_label_type",
        type: AttributeTypeEnum.DROPDOWN,
        options: Object.values(NYSLALabelTypeEnum),
        required: false,
        searchable: false,
        display_order: 16,
        is_hidden: true,
        description: "NY SLA Label type (O=Original, P=Private)",
    },
    {
        name: "nysla_distrib_id",
        type: AttributeTypeEnum.TEXT,
        options: [] as string[],
        required: false,
        searchable: false,
        display_order: 17,
        is_hidden: true,
        description: "NY SLA Distributor ID (N=None, or specific distributor ID)",
    },
];

/**
 * Combined NY SLA attribute definitions for creating category attributes
 * Includes both general wine attributes (visible) and NYSLA-specific (hidden)
 */
export const NYSLA_ATTRIBUTE_DEFINITIONS = [...WINE_GENERAL_ATTRIBUTE_DEFINITIONS, ...NYSLA_SPECIFIC_ATTRIBUTE_DEFINITIONS];

/**
 * CSV column headers in order for NY SLA batch submission
 * Total: 100 columns
 */
export const NYSLA_CSV_HEADERS = [
    // Header columns (4)
    "post_type",
    "post_month",
    "post_year",
    "wholesaler",
    // Product columns (10)
    "bev_type",
    "prod_item",
    "combo_lim",
    "brand_reg",
    "ttb_id",
    "brand_name",
    "prod_name",
    "label_type",
    "prim_info",
    "distrib_id",
    // Size columns (4)
    "item_size",
    "um",
    "botpercase",
    "subpack",
    // Wine columns (3)
    "vintage",
    "alcohol",
    "proof",
    // Price columns (4)
    "bot_price",
    "case_price",
    "bot_nyc",
    "case_nyc",
    // Distribution columns (5)
    "fullcase",
    "split_char",
    "fob",
    "nys_whole",
    "nys_item",
    // Allocation columns (6)
    "combo_disa",
    "combo_asse",
    "lim_avail",
    "alloc_det",
    "alloc_reas",
    "nys_prod",
    // Discount code (1)
    "disc_code",
    // Discount tiers 1-10 (40 columns)
    "qty_1",
    "unit_1",
    "amount_1",
    "type_1",
    "qty_2",
    "unit_2",
    "amount_2",
    "type_2",
    "qty_3",
    "unit_3",
    "amount_3",
    "type_3",
    "qty_4",
    "unit_4",
    "amount_4",
    "type_4",
    "qty_5",
    "unit_5",
    "amount_5",
    "type_5",
    "qty_6",
    "unit_6",
    "amount_6",
    "type_6",
    "qty_7",
    "unit_7",
    "amount_7",
    "type_7",
    "qty_8",
    "unit_8",
    "amount_8",
    "type_8",
    "qty_9",
    "unit_9",
    "amount_9",
    "type_9",
    "qty_10",
    "unit_10",
    "amount_10",
    "type_10",
    // Combo items 1-10 (20 columns)
    "cmbitem_1",
    "cmbqty_1",
    "cmbitem_2",
    "cmbqty_2",
    "cmbitem_3",
    "cmbqty_3",
    "cmbitem_4",
    "cmbqty_4",
    "cmbitem_5",
    "cmbqty_5",
    "cmbitem_6",
    "cmbqty_6",
    "cmbitem_7",
    "cmbqty_7",
    "cmbitem_8",
    "cmbqty_8",
    "cmbitem_9",
    "cmbqty_9",
    "cmbitem_10",
    "cmbqty_10",
];
